import { ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import Image from 'components/UI/Image';
import RichText from 'components/UI/Richtext';
import { IComponentListItemWithIconFields, IFragmentTextCallout } from 'types/contentful';
import { MAX_SCREEN_WIDTH } from 'lib/consts';

const ICON_SIZE_CONFIG = {
  Small: { wrapper: 'w-16', query: 'w=64' },
  Medium: { wrapper: 'w-24', query: 'w=96' },
  Large: { wrapper: 'w-32', query: 'w=128' },
} as const;

export default function ListItemWithIcon({
  heading,
  copy,
  copyAlignment = 'center',
  items,
  iconAlignment = 'Center',
  iconSize = 'Medium',
  iconColumn = '2',
}: IComponentListItemWithIconFields): ReactElement {
  const iconAlignmentClass = iconAlignment === 'Top' ? 'items-start' : 'items-center';
  const iconSizeConfig = ICON_SIZE_CONFIG[iconSize] ?? ICON_SIZE_CONFIG.Medium;
  const iconColumnClass =
    iconColumn === '1' ? 'md:mx-auto justify-self-center w-full lg:w-1/2' : 'md:grid-cols-2';

  const alignmentClass =
    copyAlignment === 'left'
      ? 'text-left'
      : copyAlignment === 'right'
      ? 'text-right'
      : 'text-center';

  return (
    <div className="mx-auto ecp-my text-primary-actual py-10 md:py-20 px-site-component">
      {heading && (
        <div className="flex items-center mb-8">
          <h2 className="bordered-heading bh-list-item-with-icon text-new-34 md:text-4xl text-primary-actual">
            {heading}
          </h2>
        </div>
      )}
      {copy && (
        <RichText document={copy} classNames={`text-md lg:text-lg mb-6 ${alignmentClass}`} />
      )}
      <div className={`grid grid-cols-1 ${iconColumnClass} gap-3 lg:gap-6`}>
        {items.map((item: IFragmentTextCallout) => (
          <div
            key={item.sys.id}
            className="flex flex-col lg:flex-row items-start gap-4 bg-muted p-4"
          >
            <div className={`icon section h-full flex ${iconAlignmentClass}`}>
              {item?.fields?.icon && (
                <div className={iconSizeConfig.wrapper}>
                  <Image
                    image={item?.fields?.icon}
                    query={iconSizeConfig.query ?? `w=${MAX_SCREEN_WIDTH}`}
                    classNames="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
            <div className="content section w-4/5">
              <h3 className="text-xl md:text-2xl mb-2.5 mt-0">{item?.fields?.heading}</h3>
              {item?.fields?.copy && (
                <div className="text-base lg:text-lg mt-1 mb-0">
                  <ReactMarkdown>{item.fields.copy}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
